import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { AlertTriangle, Trash2, Info } from 'lucide-react';

export interface ConfirmModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: () => void;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
}

const variantConfig = {
  danger: {
    icon: <Trash2 size={24} className="text-danger" />,
    buttonVariant: 'danger' as const,
    headerClass: 'bg-danger bg-opacity-10',
  },
  warning: {
    icon: <AlertTriangle size={24} className="text-warning" />,
    buttonVariant: 'warning' as const,
    headerClass: 'bg-warning bg-opacity-10',
  },
  info: {
    icon: <Info size={24} className="text-info" />,
    buttonVariant: 'primary' as const,
    headerClass: 'bg-info bg-opacity-10',
  },
};

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  show,
  onHide,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  isLoading = false,
}) => {
  const config = variantConfig[variant];

  const handleConfirm = () => {
    onConfirm();
    if (!isLoading) {
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="sm">
      <Modal.Header className={config.headerClass} closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          {config.icon}
          <span>{title}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-0">{message}</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={isLoading}>
          {cancelText}
        </Button>
        <Button variant={config.buttonVariant} onClick={handleConfirm} disabled={isLoading}>
          {isLoading ? 'Please wait...' : confirmText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Hook for easy confirmation modal usage
export const useConfirmModal = () => {
  const [modalState, setModalState] = React.useState<{
    show: boolean;
    title: string;
    message: string;
    confirmText: string;
    variant: 'danger' | 'warning' | 'info';
    onConfirm: () => void;
  }>({
    show: false,
    title: 'Confirm',
    message: '',
    confirmText: 'Confirm',
    variant: 'danger',
    onConfirm: () => {},
  });

  const confirm = (options: {
    title?: string;
    message: string;
    confirmText?: string;
    variant?: 'danger' | 'warning' | 'info';
    onConfirm: () => void;
  }) => {
    setModalState({
      show: true,
      title: options.title || 'Confirm',
      message: options.message,
      confirmText: options.confirmText || 'Confirm',
      variant: options.variant || 'danger',
      onConfirm: options.onConfirm,
    });
  };

  const hideModal = () => {
    setModalState(prev => ({ ...prev, show: false }));
  };

  const ConfirmModalComponent = () => (
    <ConfirmModal
      show={modalState.show}
      onHide={hideModal}
      onConfirm={modalState.onConfirm}
      title={modalState.title}
      message={modalState.message}
      confirmText={modalState.confirmText}
      variant={modalState.variant}
    />
  );

  return { confirm, ConfirmModal: ConfirmModalComponent };
};

export default ConfirmModal;
